import { useContext, useEffect, useMemo, useState } from 'react';
import { Button } from 'primereact/button';
import { GetUserCoursesApiResponseDto, MessageDto, UserCourseRecordDto, UserDto } from '../../general/dtos';
import { UserManagementDataContext } from '../../providers/user-management-provider';
import { UserAssignmentTable } from './user-assignment-table';
import { useApi } from '../../providers/api-provider';
import { UserCourseCertificateDialog, UserCourseReviewDialog } from './user-course-record-dialogs';

interface UserCourseAssignmentsProps {
    selectedUsers: UserDto[];
    saveUserCourses: (sub: string, assigned: string[]) => Promise<GetUserCoursesApiResponseDto | MessageDto | null>;
}

export function UserCourseAssignments({ selectedUsers, saveUserCourses }: UserCourseAssignmentsProps) {
    const api = useApi();
    const { allCourses, allCoursesLoading, userCourses, userCoursesLoading, userCoursesError } = useContext(UserManagementDataContext)!;
    const user = selectedUsers.length === 1 ? selectedUsers[0] : null;
    const saved = user ? userCourses[user.sub] : undefined;
    const assignments = useMemo(() => saved?.map(item => ({ key: item.course_key, assigned_at: item.assigned_at })), [saved]);
    const items = useMemo(() => allCourses.map(item => ({ key: item.course_key, name: item.name })), [allCourses]);
    const sub = user?.sub;
    const [records, setRecords] = useState<{ sub: string; data: UserCourseRecordDto[]; error: string } | null>(null);
    const [attempt, setAttempt] = useState(0);
    const [view, setView] = useState<{ sub: string; kind: 'review'; id: number } | { sub: string; kind: 'certificate'; key: string } | null>(null);
    useEffect(() => {
        if (!sub || !api) return;
        const abort = new AbortController();
        setRecords(null);
        api.getUserCourseRecords(sub, abort.signal).then(data => {
            if (!abort.signal.aborted) setRecords({ sub, data, error: '' });
        }).catch(() => {
            if (!abort.signal.aborted) setRecords({ sub, data: [], error: 'Reviews and certificates could not be loaded.' });
        });
        return () => abort.abort();
    }, [api, sub, attempt]);
    const links = useMemo(() => new Map((records && records.sub === sub ? records.data : []).map(record => [record.course_key, record])), [records, sub]);
    if (!user) return <p>{selectedUsers.length ? 'Multiple users selected' : 'No user selected'}</p>;
    const empty = records && records.sub === user.sub ? records.error ? 'Unavailable' : '—' : 'Loading...';
    return <>
    {records && records.sub === user.sub && records.error && <p role="alert">{records.error} <Button label="Retry course records" text onClick={() => setAttempt(value => value + 1)} /></p>}
    <UserAssignmentTable key={user.sub} kind="course" items={items} assignments={assignments}
        loading={allCoursesLoading || userCoursesLoading} error={userCoursesError}
        additionalColumns={[
            { header: 'Review', body: row => {
                const id = links.get(row.key)?.review_id;
                return id != null ? <Button label="View" icon="pi pi-comment" text size="small"
                    aria-label={`View review for ${items.find(item => item.key === row.key)?.name}`}
                    onClick={event => { event.stopPropagation(); setView({ sub: user.sub, kind: 'review', id }); }} /> : <span>{empty}</span>;
            } },
            { header: 'Certificate', body: row => {
                const key = links.get(row.key)?.certificate_key;
                return key ? <Button label="View" icon="pi pi-id-card" text size="small"
                    aria-label={`View certificate for ${items.find(item => item.key === row.key)?.name}`}
                    onClick={event => { event.stopPropagation(); setView({ sub: user.sub, kind: 'certificate', key }); }} /> : <span>{empty}</span>;
            } },
        ]}
        onSave={async keys => {
            const result = await saveUserCourses(user.sub, keys);
            if (!result || !('data' in result)) throw new Error(result && 'message' in result ? result.message : 'Could not save assignments. Your selections are unchanged.');
            return result.data.map(item => ({ key: item.course_key, assigned_at: item.assigned_at }));
        }} />
    {view?.sub === user.sub && (view.kind === 'review'
        ? <UserCourseReviewDialog key={`${view.sub}:review:${view.id}`} id={view.id} onClose={() => setView(null)} />
        : <UserCourseCertificateDialog key={`${view.sub}:certificate:${view.key}`} certificateKey={view.key} onClose={() => setView(null)} />)}
    </>;
}
